import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";

// API
import api from "../../../../services/api";

// Core Components
import Datatable from "../../../../core/common/dataTable/index";
import CommonSelect from "../../../../core/common/commonSelect";
import PredefinedDateRanges from "../../../../core/common/datePicker";
import ConfirmationModal from "../../../ers/common/components/ConfirmationModal";

// Services
import { notify } from "../../../../services/toast.config";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

// Component
import EditSharedFormModal from "./EditSharedFormModal";
import EmbedCodeModal from "./EmbedCodeModal";

interface SharedFormListsProps {
  selectedCompany: any;
}

const SharedFormLists: React.FC<SharedFormListsProps> = ({
  selectedCompany,
}) => {
  // 🔹 Local Storage
  const signature = localStorage.getItem("signature");

  // 🔹 State: UI
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);

  // 🔹 State: Data
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  // 🔹 State: Filters
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selectedStartDate, setSelectedStartDate] =
    useState<moment.Moment | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | null>(
    null
  );

  // 🔹 State: Modal Actions
  const [deleteRecord, setDeleteRecord] = useState<any>(null);
  const [editRecord, setEditRecord] = useState<any>(null);
  const [embedRecordUrl, setEmbedRecordUrl] = useState<string>("");

  // Derived Data
  const activeFiltersCount = [
    statusFilter,
    selectedStartDate,
    selectedEndDate,
  ].filter(Boolean).length;

  // Handlers: Form Inputs
  const handleDateChange = (start: moment.Moment, end: moment.Moment) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  };

  const handleClearFilters = () => {
    setStatusFilter("");
    setSelectedStartDate(null);
    setSelectedEndDate(null);
  };

  // Delete Handler
  const handleDelete = async () => {
    if (!deleteRecord) return;
    try {
      setIsLoading(true);
      const response = await api.post("leadService/forms/share-links/delete", {
        signature,
        company_id: deleteRecord?.company_id,
        company_type: deleteRecord?.company_type,
        link_id: deleteRecord?.id,
      });
      notify.success(
        response.data?.message || "Shared form link deleted successfully."
      );
    } catch (error: any) {
      notify.error(
        error?.response?.data?.message ||
        error?.data?.message ||
        "An error occurred."
      );
    } finally {
      setDeleteRecord(null);
      setIsDeleteModalOpen(false);
      await updateUI();
      setIsLoading(false);
    }
  };

  const handleDeleteModalOpen = useCallback((record: any) => {
    setDeleteRecord(record);
    setIsDeleteModalOpen(true);
  }, []);

  const handleEditModalOpen = useCallback((record: any) => {
    setEditRecord(record);
    setIsEditModalOpen(true);
  }, []);

  // Close delete modal
  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteRecord(null);
  }, []);

  // Close edit modal
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditRecord(null);
  }, []);

  const handleEmbedModalOpen = useCallback((record: any) => {
    setEmbedRecordUrl(record.shareable_url);
    setIsEmbedModalOpen(true);
  }, []);

  const handleCloseEmbedModal = useCallback(() => {
    setIsEmbedModalOpen(false);
    setEmbedRecordUrl("");
  }, []);

  // Fetch Data
  const getAllSharedFormsData = async () => {
    try {
      setIsLoading(true);
      const response = await api.post("leadService/forms/share-links/list", {
        signature,
        company_id: selectedCompany?.companyId,
        company_type: selectedCompany?.companytype,
      });
      const result = response?.data?.data || [];
      setData(result);
      setFilteredData(result);
    } catch (error) {
      notify.error("Failed to load shared forms!");
    } finally {
      setIsLoading(false);
    }
  };

  const updateUI = async () => {
    const startTime = Date.now();

    try {
      await getAllSharedFormsData();
    } catch (error) {
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 300 - elapsed);
      if (remaining > 0) {
        setTimeout(() => setIsLoading(false), remaining);
      } else {
        setIsLoading(false);
      }
    }
  };

  // Handle Share Form (Copy to Clipboard)
  const handleShareForm = (record: any) => {
    navigator.clipboard.writeText(record.shareable_url);
    notify.success("Shareable link copied to clipboard!");
  };

  // Use Effects
  useEffect(() => {
    if (selectedCompany?.companyId && selectedCompany?.companytype) {
      updateUI();
    }
  }, [selectedCompany?.companyId, selectedCompany?.companytype]);

  useEffect(() => {
    let newFilteredData = [...data];

    if (statusFilter) {
      newFilteredData = newFilteredData.filter(
        (item) => item.status_text === statusFilter
      );
    }

    if (selectedStartDate && selectedEndDate) {
      newFilteredData = newFilteredData.filter((item) => {
        const createdAt = new Date(item.created_at);
        return (
          createdAt >= selectedStartDate.toDate() &&
          createdAt <= selectedEndDate.toDate()
        );
      });
    }

    setFilteredData(newFilteredData);
  }, [statusFilter, selectedStartDate, selectedEndDate, data]);

  const columns = [
    {
      title: "Lead Form Name",
      dataIndex: "form_name",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <h6 className="fw-medium fs-14 table-text-wrap">{text}</h6>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.form_name || "").localeCompare(b?.form_name || ""),
    },
    {
      title: "Form Title",
      dataIndex: "title",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          {text ? (
            <h6 className="fw-medium fs-14 table-text-wrap">{text}</h6>
          ) : (
            <h6 className="fw-normal fs-13 text-muted">No Title</h6>
          )}
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.title || "").localeCompare(b?.title || ""),
    },
    {
      title: "Public Link",
      dataIndex: "shareable_url",
      render: (text: string, record: any) => (
        <div className="d-flex justify-content-between align-items-center">
          <a
            className="text-info me-2 text-truncate"
            href={text}
            target="_blank"
            rel="noopener noreferrer"
            style={{ maxWidth: "300px" }}>
            {text}
          </a>
          <Link
            title="Copy Form Link"
            to="#"
            className="btn btn-outline-success btn-sm"
            onClick={(e) => {
              e.preventDefault();
              handleShareForm(record);
            }}>
            <i className="ti ti-copy me-1" />
            Copy
          </Link>
        </div>
      ),
    },
    {
      title: "Submission Count",
      dataIndex: "submission_count",
      align: "center",
      render: (text: number) => (
        <span className="badge badge-soft-info">{text}</span>
      ),
      sorter: (a: any, b: any) => a?.submission_count - b?.submission_count,
    },
    {
      title: "Status",
      dataIndex: "status_text",
      render: (text: string) => (
        <span
          className={`badge d-inline-flex align-items-center badge-xs ${text === "Active" ? "badge-success" : "badge-danger"
            }`}>
          <i className="ti ti-point-filled me-1"></i>
          {text}
        </span>
      ),
      sorter: (a: any, b: any) =>
        (a?.status_text || "").localeCompare(b?.status_text || ""),
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      render: (text: string) => <span>{humanReadableDate(text)}
      </span>,
      sorter: (a: any, b: any) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: "Link Website",
      dataIndex: "id",
      align: "center",
      render: (_: any, record: any) => (
        <button
          className="btn btn-secondary btn-sm d-inline-flex align-items-center"
          onClick={(e) => {
            e.preventDefault();
            handleEmbedModalOpen(record);
          }}
          title="Get Embed Code">
          <i className="ti ti-code me-1" />
          Embed
        </button>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            title="Edit Shared Form"
            onClick={(e) => {
              e.preventDefault();
              handleEditModalOpen(record);
            }}>
            <i className="ti ti-edit" />
          </Link>
          <Link
            to="#"
            title="Delete Shared Form"
            onClick={(e) => {
              e.preventDefault();
              handleDeleteModalOpen(record);
            }}>
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h5>Shared Forms List</h5>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
            <div
              className="me-3"
              style={{ minWidth: "120px" }}
              title="Status Filter">
              <CommonSelect
                options={[
                  { value: "", label: "All" },
                  { value: "Active", label: "Active" },
                  { value: "Inactive", label: "Inactive" },
                ]}
                defaultValue={
                  statusFilter
                    ? { value: statusFilter, label: statusFilter }
                    : { value: "", label: "All" }
                }
                onChange={(selected: any) =>
                  setStatusFilter(selected?.value || "")
                }
                placeHolder="Select Status"
              />
            </div>

            <div className="me-3">
              <div
                className="d-flex align-items-center"
                title="Date Range Filter">
                <PredefinedDateRanges onDateChange={handleDateChange} />
              </div>
            </div>

            <div className="me-2" title="Clear Filters">
              <button
                style={{ width: "120px" }}
                className="btn btn-outline-danger d-flex align-items-center position-relative justify-content-center"
                onClick={handleClearFilters}
                disabled={activeFiltersCount === 0}>
                <i className="ti ti-x me-2" />
                Clear
                {activeFiltersCount > 0 && (
                  <span className="badge bg-danger rounded-pill ms-2">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          {isLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}>
              <div className="text-center">
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading shared forms...</p>
              </div>
            </div>
          ) : (
            <Datatable dataSource={filteredData} columns={columns} />
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        message="Are you sure you want to delete this shared form link? This action cannot be undone."
        title="Delete Shared Form Link"
        onConfirm={handleDelete}
        type="danger"
      />

      {/* Edit Modal */}
      {isEditModalOpen && (
        <EditSharedFormModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          selectedRecord={editRecord}
          selectedCompany={selectedCompany}
          updateUI={updateUI}
        />
      )}

      {/* 🔹 Embed Code Modal */}
      <EmbedCodeModal
        isOpen={isEmbedModalOpen}
        onClose={handleCloseEmbedModal}
        shareableUrl={embedRecordUrl}
      />
    </>
  );
};

export default SharedFormLists;
